import { parseCalendarDate } from "../../utils/date";
import { matchLocation } from "../Matches/matchDiscovery";
import { entryResult, groupMyMatches, statusValue } from "./myMatches";

export const resultClaimKey = claim => `${claim.family}:${claim.matchId}:${claim.participantId}`;
export const isClaimedEntry = entry => entry.kind === "claim";
export const historyEntryName = entry => isClaimedEntry(entry)
  ? (entry.claim.availability === "available" && entry.claim.participant?.displayName) || "Claimed result"
  : entry.registration?.displayName || "Your entry";
export const historyEntryKey = entry => isClaimedEntry(entry)
  ? `claim:${resultClaimKey(entry.claim)}` : `${entry.family}:${entry.registration.registrationId}`;
export const claimUnavailableLabel = claim => claim.availability === "result_unavailable"
  ? "Result no longer available" : "Match no longer available";

export function historyEntryResult(entry) {
  if (!isClaimedEntry(entry)) return entryResult(entry);
  if (entry.claim.availability !== "available") return { value: claimUnavailableLabel(entry.claim), detail: "You can remove this claim from your history." };
  const result = entry.claim.result;
  const outcome = String(result?.outcomeStatus || "").toLowerCase();
  return entryResult({ family: entry.family, scoreSummary: {
    match: result ? { ...result, status: ["dq", "dnf", "withdrawn"].includes(outcome) ? outcome : result.status } : null,
    stagesCompleted: result?.stagesCompleted, expectedStageCount: result?.expectedStageCount,
  } });
}

// This loader intentionally has no ownership inference. Authentication scopes the
// claims endpoint, while registrations retain their separate self_service filter.
export async function loadCompleteResultClaims(loadPage, signal) {
  const rows = new Map();
  const tokens = new Set();
  let nextToken;
  try {
    do {
      const page = await loadPage({ limit: 25, ...(nextToken ? { nextToken } : {}), ...(signal ? { signal } : {}) });
      if (!page || !Array.isArray(page.data)) throw new Error("Claimed results could not load. Try again.");
      for (const claim of page.data) {
        if (!["arc", "bullseye"].includes(claim.family) || !claim.matchId || !claim.participantId) throw new Error("A claimed result is missing its identity. Retry loading history.");
        rows.set(resultClaimKey(claim), claim);
      }
      nextToken = page.nextToken || page.meta?.nextToken;
      if (nextToken && (tokens.has(nextToken) || tokens.size >= 199)) throw new Error("Claimed results pagination did not finish. Retry loading history.");
      if (nextToken) tokens.add(nextToken);
    } while (nextToken);
    return { data: [...rows.values()], error: null };
  } catch (error) {
    if (!rows.size) throw error;
    return { data: [...rows.values()], error };
  }
}

export function groupPersonalMatches(registrations, claims, now = new Date()) {
  const registered = groupMyMatches(registrations, now);
  const past = new Map(registered.past.map(group => [group.key, { ...group, entries: group.entries.map(entry => ({ ...entry })) }]));
  for (const claim of claims) {
    const key = `${claim.family}:${claim.matchId}`;
    const available = claim.availability === "available";
    if (!past.has(key)) {
      // Never use stale metadata returned accidentally with an unavailable link.
      const match = available ? claim.match || {} : {};
      past.set(key, {
        key, family: claim.family, match, matchId: claim.matchId,
        name: available ? match.matchName || "Past match" : claimUnavailableLabel(claim),
        date: parseCalendarDate(match.startDate || match.matchDate), phase: "completed", entries: [],
        path: available ? `/matches/${claim.family}/${encodeURIComponent(claim.matchId)}` : null,
        discipline: claim.family === "bullseye" ? "Bullseye" : "NRA ARC",
      });
    }
    const group = past.get(key);
    const registration = group.entries.find(entry => !isClaimedEntry(entry) && entry.registration?.participantId === claim.participantId);
    if (registration) registration.resultClaim = claim;
    else group.entries.push({ kind: "claim", family: claim.family, claim });
    group.claimedAt = [group.claimedAt, claim.claimedAt].filter(Boolean).sort().pop();
    // A match can contain both an unavailable participant and another public one.
    if (available && !group.path) {
      group.match = claim.match || {};
      group.name = group.match.matchName || "Past match";
      group.date = parseCalendarDate(group.match.startDate || group.match.matchDate);
      group.path = `/matches/${claim.family}/${encodeURIComponent(claim.matchId)}`;
    }
  }
  const pastGroups = [...past.values()].sort((a, b) => (b.date?.getTime() || Date.parse(b.claimedAt) || 0)
    - (a.date?.getTime() || Date.parse(a.claimedAt) || 0) || a.name.localeCompare(b.name));
  return { ...registered, past: pastGroups, all: [...registered.upcoming, ...pastGroups, ...registered.inactive] };
}

export function filterPersonalHistory(groups, { query = "", family = "all", status = "all", association = "all" }) {
  return groups.flatMap(group => {
    if (family !== "all" && family !== group.family) return [];
    const entries = group.entries.filter(entry => {
      if (association === "registered" && isClaimedEntry(entry)) return false;
      if (association === "claimed" && !isClaimedEntry(entry) && !entry.resultClaim) return false;
      return status === "all" || (!isClaimedEntry(entry) && statusValue(entry) === status);
    });
    if (!entries.length) return [];
    const search = query.trim().toLowerCase();
    if (search && ![group.name, group.discipline, matchLocation(group.match), ...entries.map(historyEntryName)]
      .some(value => String(value || "").toLowerCase().includes(search))) return [];
    return [{ ...group, entries }];
  });
}
